import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { z } from "zod";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const requestSchema = z.object({
  id: z.string(),
  body: z.string(),
  mayContact: z.boolean(),
  confidential: z.boolean(),
  status: z.enum(["new", "assigned", "in_progress", "closed"]),
  hasOwner: z.boolean(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
});
const schema = z.object({ linked: z.boolean(), requests: z.array(requestSchema) });
export type MyCareRequest = z.infer<typeof requestSchema>;

/** What I asked and where it stands (D13): a status, never a name. */
export function useMyCareRequests() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["my-care-requests", tenantId],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("my_care_requests", {
        p_tenant_id: tenantId ?? "",
      });
      if (error) throw error;
      return schema.parse(data);
    },
  });
}

export function useSubmitCareRequest() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string; mayContact: boolean; confidential: boolean }) => {
      const { error } = await supabase.rpc("submit_care_request", {
        p_tenant_id: tenantId ?? "",
        p_body: input.body,
        p_may_contact: input.mayContact,
        p_confidential: input.confidential,
      });
      if (error) throw error;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ["my-care-requests", tenantId] }),
  });
}
